import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import 'react-phone-number-input/style.css';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/input';

import { useInputId } from 'partial/components/form/hooks';

const CustomInput = React.forwardRef((props, ref) => (
  <input
    ref={ref}
    {...props}
    className={cn(
      'peer block w-full border-0 p-0 text-gray-900 placeholder-gray-500 focus:ring-0',
      'focus:outline-none',
    )}
  />
));

function InputNumber({
  id: defaultId,
  icon: Icon,
  label,
  name,
  onChange,
  onSetFieldValue,
  value,
  error,
  required,
  readOnly,
}) {
  const [id] = useInputId(defaultId);
  const handleChange = (v) => {
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: v,
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, v ?? '');
    }
  };
  const showIcon = Icon || error;
  const isError = !!error;
  const isPhoneValid = isValidPhoneNumber(value || '') || !value;
  return (
    <div className="space-y-2">
      <label
        htmlFor={id}
        className="pl-px uppercase text-gray-300 font-semibold"
      >
        <span>{label}</span>
        {required && <span className="ml-1 font-light text-red-500">*</span>}
      </label>
      <div
        className={cn(
          'bg-white relative border rounded px-3 py-2 focus-within:ring-1 z-10',
          isError || !isPhoneValid
            ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500'
            : 'border-gray-200 focus-within:ring-primary-600 focus-within:border-primary-600',
        )}
      >
        <PhoneInput
          id={id}
          defaultCountry="PH"
          value={value || ''}
          onChange={handleChange}
          placeholder="09XX XXXX XXX"
          readOnly={readOnly}
          inputComponent={CustomInput}
        />
        {showIcon && (
          <div
            className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            title={error || ''}
          >
            {error ? (
              <AiOutlineExclamationCircle
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            ) : (
              <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

InputNumber.defaultProps = {
  id: '',
  icon: false,
  error: null,
  required: false,
  onChange: false,
  onSetFieldValue: false,
  readOnly: false,
};

InputNumber.propTypes = {
  id: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.bool]),
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  readOnly: PropTypes.bool,
};

export default React.memo(InputNumber);
